import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";
import { useCatalog } from "@/context/CatalogContext";
import { LoadingState, PageHeader, StatePill, apiErrorMessage } from "@/components/admin/AdminUI";

/** Matriz somente leitura: o que cada perfil recebe por padrão (contrato completo). */
export default function AdminProfiles() {
  const { isAvailable } = useCatalog();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin-center/profiles").then(({ data: payload }) => setData(payload)).catch((err) => setError(apiErrorMessage(err)));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data) return <LoadingState />;
  return (
    <div className="space-y-5" data-testid="admin-profiles">
      <PageHeader
        icon={UserCog}
        title="Perfis"
        description="Módulos que cada perfil recebe por padrão quando a empresa os contrata. Ajustes individuais são feitos no usuário."
      />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data.profiles.map((profile) => (
          <div key={profile.key} className="rounded-xl border border-border bg-card p-4" data-testid={`profile-card-${profile.key}`}>
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">{ROLE_LABELS[profile.key] || profile.label}</h2>
              {profile.platform ? <StatePill tone="info">Plataforma</StatePill> : profile.manages_users ? <StatePill tone="warning">Administra usuários</StatePill> : null}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{profile.description}</p>
            {profile.delegable_profiles.length > 0 && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Pode administrar: {profile.delegable_profiles.map((key) => ROLE_LABELS[key] || key).join(", ")}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-xs" data-testid="profiles-matrix">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="sticky left-0 bg-muted/40 px-3 py-2 text-left font-medium">Módulo</th>
              {data.profiles.map((profile) => (
                <th key={profile.key} className="px-2 py-2 font-medium">{ROLE_LABELS[profile.key] || profile.key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.catalog.map((entry) => (
              <tr key={entry.key} className="border-b border-border/60 last:border-0">
                <td className="sticky left-0 bg-card px-3 py-1.5">
                  {entry.label}
                  {entry.platform_reserved && <span className="ml-1 text-[10px] text-muted-foreground">(plataforma)</span>}
                </td>
                {data.profiles.map((profile) => (
                  <td key={profile.key} className="px-2 py-1.5 text-center">
                    {profile.entries[entry.key] ? (
                      <Check size={14} className="mx-auto text-emerald-600" aria-label="disponível" />
                    ) : (
                      <Minus size={14} className="mx-auto text-muted-foreground/50" aria-label="indisponível" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isAvailable("administracao_plataforma") && (
        <p className="text-xs text-muted-foreground">
          A matriz global de ações por perfil é publicada em{" "}
          <Link className="text-primary underline" to="/administracao-plataforma">Administração da Plataforma</Link>.
        </p>
      )}
    </div>
  );
}
